/**
 * @fileoverview APIクライアント
 * @description バックエンドAPIとの通信を行うクライアント
 */
import type { ApiResponse, ChatRequest, ChatResponse, PromptTemplate } from '@karosorabei/shared'

/**
 * APIのベースURL
 * @description 環境変数から取得、未設定の場合は相対パスを使用
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

/**
 * APIエラークラス
 * @description API呼び出し時のエラーを表現
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * 共通のfetch処理
 * @description 全てのAPI呼び出しで共通のエラーハンドリングを行う
 * @param endpoint - APIエンドポイント（/api/xxxの形式）
 * @param options - fetchオプション
 * @returns レスポンスデータ
 * @throws ApiError - API呼び出し失敗時
 */
// eslint-disable-next-line no-undef -- RequestInit はブラウザ組み込み型
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    // Cookie（セッション）を送信
    credentials: 'include',
  })

  // JSONレスポンスをパース
  const data = (await response.json()) as ApiResponse<T>

  // エラーレスポンスの処理
  if (!response.ok || !data.success) {
    throw new ApiError(data.error ?? 'Unknown error occurred', response.status, undefined)
  }

  return data.data as T
}

/**
 * チャットAPI
 * @description AIチャット機能のAPI呼び出し
 */
export const chatApi = {
  /**
   * チャットメッセージを送信
   * @param request - チャットリクエスト
   * @returns チャットレスポンス
   */
  send: async (request: ChatRequest): Promise<ChatResponse> => {
    return fetchApi<ChatResponse>('/api/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  },

  /**
   * FAQテンプレート一覧を取得
   * @returns テンプレート一覧
   */
  getTemplates: async (): Promise<PromptTemplate[]> => {
    return fetchApi<PromptTemplate[]>('/api/chat/templates')
  },
}

/**
 * ヘルスチェックAPI
 * @description サーバーの状態を確認
 */
export const healthApi = {
  /**
   * ヘルスチェックを実行
   * @returns ステータス情報
   */
  check: async (): Promise<{ status: string; timestamp: string }> => {
    return fetchApi<{ status: string; timestamp: string }>('/api/health')
  },
}

/**
 * 認証API
 * @description OAuth認証関連のAPI呼び出し
 */
export const authApi = {
  /**
   * 現在のユーザー情報を取得
   * @returns ユーザー情報（未認証の場合はnull）
   */
  getMe: async (): Promise<{ id: string; email: string; name?: string } | null> => {
    try {
      return await fetchApi<{ id: string; email: string; name?: string }>('/api/auth/me')
    } catch (error) {
      // 401エラーは未認証として処理
      if (error instanceof ApiError && error.status === 401) {
        return null
      }
      throw error
    }
  },

  /**
   * ログアウト
   */
  logout: async (): Promise<void> => {
    await fetchApi<void>('/api/auth/logout', {
      method: 'POST',
    })
  },
}
